import React, { useEffect, useState, createElement, Component } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Calculator, Download, PieChart, TrendingUp, DollarSign } from 'lucide-react';
interface AmortizationRow {
  month: number;
  openingBalance: number;
  emi: number;
  principal: number;
  interest: number;
  closingBalance: number;
  totalInterest: number;
}
export function EmiCalculator() {
  const [loanAmount, setLoanAmount] = useState('500000');
  const [interestRate, setInterestRate] = useState('8.5');
  const [tenure, setTenure] = useState('5');
  const [tenureType, setTenureType] = useState<'years' | 'months'>('years');
  const [loanType, setLoanType] = useState('home');
  const [interestMethod, setInterestMethod] = useState('reducing');
  const [result, setResult] = useState<{
    emi: number;
    totalInterest: number;
    totalPayment: number;
    schedule: AmortizationRow[];
  } | null>(null);
  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const ratePerYear = parseFloat(interestRate);
    const timeInMonths = tenureType === 'years' ? parseFloat(tenure) * 12 : parseFloat(tenure);
    if (isNaN(principal) || isNaN(ratePerYear) || isNaN(timeInMonths) || principal <= 0 || timeInMonths <= 0) {
      return;
    }
    let emi = 0;
    let totalInterest = 0;
    let totalPayment = 0;
    const schedule: AmortizationRow[] = [];
    if (interestMethod === 'flat') {
      // Flat Rate Method
      totalInterest = principal * ratePerYear * (timeInMonths / 12) / 100;
      totalPayment = principal + totalInterest;
      emi = totalPayment / timeInMonths;
      let balance = principal;
      let interestPaid = 0;
      const monthlyPrincipal = principal / timeInMonths;
      const monthlyInterest = totalInterest / timeInMonths;
      for (let i = 1; i <= timeInMonths; i++) {
        const opening = balance;
        balance = Math.max(0, balance - monthlyPrincipal);
        interestPaid += monthlyInterest;
        schedule.push({
          month: i,
          openingBalance: opening,
          emi: emi,
          principal: monthlyPrincipal,
          interest: monthlyInterest,
          closingBalance: balance,
          totalInterest: interestPaid
        });
      }
    } else {
      // Reducing Balance Method (Standard)
      const r = ratePerYear / 12 / 100;
      emi = principal * r * Math.pow(1 + r, timeInMonths) / (Math.pow(1 + r, timeInMonths) - 1);
      let balance = principal;
      let interestPaid = 0;
      for (let i = 1; i <= timeInMonths; i++) {
        const opening = balance;
        const interest = balance * r;
        const principalComponent = emi - interest;
        balance = Math.max(0, balance - principalComponent);
        interestPaid += interest;
        schedule.push({
          month: i,
          openingBalance: opening,
          emi: emi,
          principal: principalComponent,
          interest: interest,
          closingBalance: balance,
          totalInterest: interestPaid
        });
      }
      totalPayment = emi * timeInMonths;
      totalInterest = totalPayment - principal;
    }
    setResult({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalPayment: Math.round(totalPayment),
      schedule
    });
  };
  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, tenure, tenureType, loanType, interestMethod]);
  const exportToCSV = () => {
    if (!result) return;
    const headers = ['Month,Opening Balance,EMI,Principal,Interest,Closing Balance,Total Interest Paid'];
    const rows = result.schedule.map(row => `${row.month},${row.openingBalance.toFixed(2)},${row.emi.toFixed(2)},${row.principal.toFixed(2)},${row.interest.toFixed(2)},${row.closingBalance.toFixed(2)},${row.totalInterest.toFixed(2)}`);
    const csvContent = 'data:text/csv;charset=utf-8,' + headers.concat(rows).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `emi_schedule_${loanType}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  return <div className="container mx-auto px-4 py-8 max-w-6xl" data-id="element-205">
      <div className="mb-8" data-id="element-206">
        <h1 className="text-4xl font-bold mb-2 text-gradient" data-id="element-207">
          EMI Calculator
        </h1>
        <p className="text-muted-foreground" data-id="element-208">
          Calculate EMI for Home, Car, Personal, and Education loans with
          detailed schedule
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-209">
        {/* Input Section */}
        <Card className="lg:col-span-1 h-fit card-hover border-primary/20" data-id="element-210">
          <CardHeader data-id="element-211">
            <CardTitle className="flex items-center gap-2" data-id="element-212">
              <Calculator className="w-5 h-5 text-primary" data-id="element-213" />
              Loan Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-214">
            <Select label="Loan Type" value={loanType} onChange={e => setLoanType(e.target.value)} options={[{
            value: 'home',
            label: 'Home Loan'
          }, {
            value: 'car',
            label: 'Car Loan'
          }, {
            value: 'personal',
            label: 'Personal Loan'
          }, {
            value: 'education',
            label: 'Education Loan'
          }]} data-id="element-215" />

            <Input type="number" label="Loan Amount (₹)" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} placeholder="500000" data-id="element-216" />

            <Input type="number" label="Interest Rate (% p.a.)" value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="8.5" step="0.1" data-id="element-217" />

            <div className="flex gap-2 items-end" data-id="element-218">
              <div className="flex-1" data-id="element-219">
                <Input type="number" label="Tenure" value={tenure} onChange={e => setTenure(e.target.value)} placeholder="5" data-id="element-220" />
              </div>
              <div className="w-32" data-id="element-221">
                <Select value={tenureType} onChange={e => setTenureType(e.target.value as 'years' | 'months')} options={[{
                value: 'years',
                label: 'Years'
              }, {
                value: 'months',
                label: 'Months'
              }]} data-id="element-222" />
              </div>
            </div>

            <Select label="Interest Method" value={interestMethod} onChange={e => setInterestMethod(e.target.value)} options={[{
            value: 'reducing',
            label: 'Reducing Balance (Standard)'
          }, {
            value: 'flat',
            label: 'Flat Rate'
          }]} data-id="element-223" />
          </CardContent>
        </Card>

        {/* Results Section */}
        <div className="lg:col-span-2 space-y-6" data-id="element-224">
          {result && <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-225">
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 shadow-lg" data-id="element-226">
                  <CardContent className="p-6 text-center" data-id="element-227">
                    <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-228">
                      Monthly EMI
                    </p>
                    <p className="text-3xl font-bold text-primary" data-id="element-229">
                      {formatCurrency(result.emi)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20" data-id="element-230">
                  <CardContent className="p-6 text-center" data-id="element-231">
                    <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-232">
                      Total Interest
                    </p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-233">
                      {formatCurrency(result.totalInterest)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20" data-id="element-234">
                  <CardContent className="p-6 text-center" data-id="element-235">
                    <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-236">
                      Total Payment
                    </p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-237">
                      {formatCurrency(result.totalPayment)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Visual Chart */}
              <Card data-id="element-238">
                <CardHeader data-id="element-239">
                  <CardTitle className="flex items-center gap-2" data-id="element-240">
                    <PieChart className="w-5 h-5" data-id="element-241" />
                    Payment Breakup
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-242">
                  <div className="flex items-center gap-4 mb-4" data-id="element-243">
                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden flex" data-id="element-244">
                      <div className="h-full bg-green-500" style={{
                    width: `${parseFloat(loanAmount) / result.totalPayment * 100}%`
                  }} title={`Principal: ${(parseFloat(loanAmount) / result.totalPayment * 100).toFixed(1)}%`} data-id="element-245" />
                      <div className="h-full bg-orange-500" style={{
                    width: `${result.totalInterest / result.totalPayment * 100}%`
                  }} title={`Interest: ${(result.totalInterest / result.totalPayment * 100).toFixed(1)}%`} data-id="element-246" />
                    </div>
                  </div>
                  <div className="flex justify-center gap-8 text-sm" data-id="element-247">
                    <div className="flex items-center gap-2" data-id="element-248">
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-249" />
                      <span data-id="element-250">
                        Principal (
                        {(parseFloat(loanAmount) / result.totalPayment * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-251">
                      <div className="w-3 h-3 rounded-full bg-orange-500" data-id="element-252" />
                      <span data-id="element-253">
                        Interest (
                        {(result.totalInterest / result.totalPayment * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}
        </div>
      </div>

      {/* Amortization Table */}
      {result && <Card className="overflow-hidden" data-id="element-254">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-255">
            <div data-id="element-256">
              <CardTitle className="flex items-center gap-2" data-id="element-257">
                <TrendingUp className="w-5 h-5" data-id="element-258" />
                Amortization Schedule
              </CardTitle>
              <CardDescription data-id="element-259">Month-wise repayment details</CardDescription>
            </div>
            <Button onClick={exportToCSV} variant="outline" size="sm" data-id="element-260">
              <Download className="w-4 h-4 mr-2" data-id="element-261" />
              Export CSV
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-262">
            <div className="table-container max-h-[600px] overflow-y-auto" data-id="element-263">
              <table className="data-table" data-id="element-264">
                <thead data-id="element-265">
                  <tr data-id="element-266">
                    <th data-id="element-267">Month</th>
                    <th data-id="element-268">Opening Balance</th>
                    <th data-id="element-269">EMI</th>
                    <th data-id="element-270">Principal</th>
                    <th data-id="element-271">Interest</th>
                    <th data-id="element-272">Closing Balance</th>
                  </tr>
                </thead>
                <tbody data-id="element-273">
                  {result.schedule.map(row => <tr key={row.month} data-id="element-274">
                      <td className="font-medium text-center" data-id="element-275">{row.month}</td>
                      <td data-id="element-276">{formatCurrency(row.openingBalance)}</td>
                      <td className="font-medium" data-id="element-277">{formatCurrency(row.emi)}</td>
                      <td className="text-green-600 dark:text-green-400 font-medium" data-id="element-278">
                        {formatCurrency(row.principal)}
                      </td>
                      <td className="text-orange-600 dark:text-orange-400 font-medium" data-id="element-279">
                        {formatCurrency(row.interest)}
                      </td>
                      <td data-id="element-280">{formatCurrency(row.closingBalance)}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>}
    </div>;
}